/**
 * #6248: what the start-up does with a singleton document it could not read.
 *
 * On a device's first start the singleton documents (settings, the current plan, fitness
 * state, the schedule and the rest) are read through the READ database, which is the server
 * until this device has finished a download (#5068). A dropped request used to leave the
 * document at its empty default for the whole session, because nothing ever puts the real
 * document into the copy the app holds afterwards: the initial sync writes it to this device's
 * local database and nowhere else. The owner's rule since 2026-10-07 is that a session never
 * starts on an empty default. A copy from this device is acceptable; nothing at all is not.
 *
 * So a document the boot read could not get is looked for in this device's own database,
 * first straight away and then, for whatever is still missing, once the singleton pre-sync
 * (the small sync that downloads just these documents) has had a chance to finish. Whatever is
 * still missing after that is reported back as unreadable, and the cold start holds the
 * loading screen rather than show an account built from defaults.
 *
 * This module holds the decision only, so it can be tested without a database. The wrapper
 * supplies the reads and the wait. See design-docs/pass-through-db.md, "A singleton the boot
 * could not read".
 */

import { InitialSyncWaitResult } from './user-db.task-status';

/** What one boot read of a singleton document found. */
export enum BootSingletonReadKind {
  /** the read answered with the document */
  Found = 'found',
  /** the read answered that the document does not exist: a new account, its default is right */
  NotFound = 'not-found',
  /** the read did not answer: a dropped request, or a database recovery took the failure */
  Unreadable = 'unreadable',
}

export type BootSingletonRead<T> =
  | { kind: BootSingletonReadKind.Found; doc: T }
  | { kind: BootSingletonReadKind.NotFound }
  | { kind: BootSingletonReadKind.Unreadable };

export interface DeviceCopyFallbackDeps {
  /**
   * Read these ids from this device's own database and answer the ones it holds, by id.
   * Throws when the local store could not be read at all.
   */
  readLocalAsync: (ids: string[]) => Promise<Map<string, any>>;
  /**
   * Wait for the singleton pre-sync to finish, success or failure, giving up after a cap.
   * Only called when the first local read left something missing.
   */
  waitForSingletonPreSyncAsync: () => Promise<InitialSyncWaitResult>;
}

export interface DeviceCopyFallbackResult {
  /** the documents found in this device's database, by id */
  found: Map<string, any>;
  /**
   * missing locally after a pre-sync that SUCCEEDED. That sync downloads every one of these
   * documents the server holds, so the server does not hold these: their default is right.
   */
  notFound: string[];
  /** still unknown: the start-up must not hand these out as empty defaults */
  stillUnreadable: string[];
  /** how the wait for the pre-sync ended, or null when nothing needed the wait */
  waitResult: InitialSyncWaitResult | null;
  /** the error of the last local read that failed outright, or null */
  localReadError: unknown;
}

/**
 * Look for the documents the boot read could not get in this device's own database: once
 * straight away, then once more after waiting for the singleton pre-sync. Never throws.
 */
export async function readUnreadableSingletonsFromDeviceAsync(
  ids: string[],
  deps: DeviceCopyFallbackDeps,
): Promise<DeviceCopyFallbackResult> {
  const found = new Map<string, any>();
  let localReadError: unknown = null;

  const readIntoFoundAsync = async (wanted: string[]): Promise<boolean> => {
    try {
      const docs = await deps.readLocalAsync(wanted);
      for (const id of wanted) {
        const doc = docs.get(id);
        if (doc != null) found.set(id, doc);
      }
      return true;
    } catch (err) {
      localReadError = err;
      return false;
    }
  };

  // if this device already holds them (a device that has synced before), nothing needs to wait
  await readIntoFoundAsync(ids);
  const missing = ids.filter(id => !found.has(id));
  if (missing.length === 0) {
    return { found, notFound: [], stillUnreadable: [], waitResult: null, localReadError };
  }

  // otherwise give the pre-sync its chance to bring them down, then look again
  const waitResult = await deps.waitForSingletonPreSyncAsync();
  const secondReadWorked = await readIntoFoundAsync(missing);

  const notFound: string[] = [];
  const stillUnreadable: string[] = [];
  for (const id of missing) {
    if (found.has(id)) continue;
    // if the pre-sync finished and the local store answered, an absent document is one the
    // server does not have either; anything less than that is not an answer
    if (waitResult === InitialSyncWaitResult.Succeeded && secondReadWorked) {
      notFound.push(id);
    } else {
      stillUnreadable.push(id);
    }
  }
  return { found, notFound, stillUnreadable, waitResult, localReadError };
}
